<template>
  <div>
    <div v-if="!$route.meta.hideCreditHeader">
      <h6 class="text-uppercase font-weight-bold">
        {{ wrapperTitle }}
      </h6>
      <div class="custom-mb">
        <MobialisProgressBar
          v-if="progressBarData.navInfo && progressBarData.numberOfSteps"
          :numberOfSteps="progressBarData.numberOfSteps"
          :navInfo="progressBarData.navInfo"
        >
        </MobialisProgressBar>
        <MobialisHorizontalRuleHeader v-else> </MobialisHorizontalRuleHeader>
        <div v-if="displayVehicleName && displayVehicleName !== ''" class="font-weight-bold mt-3">
          {{ displayVehicleName }}
        </div>
      </div>
    </div>
    <router-view name="hero"></router-view>
    <router-view></router-view>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import helperFunctions from '@/utils/helperFunctions';
import creditDataSourceCA from '@/utils/enums/creditDataSourceCA';
import TradeInSource from '@/utils/enums/tradeInSource';

export default {
  name: 'CreditWrapper',
  computed: {
    ...mapGetters('business', [
      'isPrequalificationEnabled',
      'prequalificationTradeInEquityAssessmentEnabled',
      'prequalificationIncomeAndDebtEnabled',
      'showSiriusXMBanner',
      'prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled',
      'getDefaultCreditDataSourceCA',
    ]),
    tradeInSourceIsCarfax() {
      return this.$store.getters['business/getTradeInSource'] === TradeInSource.CARFAX;
    },
    wrapperTitle() {
      if (helperFunctions.isAVFinanceFlow(this.$route.path)) {
        return this.$t('message.credit.financeApplication');
      }
      if (this.isPrequalificationEnabled) {
        return this.$t('message.credit.application.preQualification.creditWrapperTitle');
      }
      if (!this.isPrequalificationEnabled) {
        if (this.getDefaultCreditDataSourceCA === creditDataSourceCA.EQUIFAX) {
          return this.$t('message.credit.credit');
        }
        if (this.getDefaultCreditDataSourceCA === creditDataSourceCA.EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT) {
          return this.$t('message.credit.creditCheck');
        }
      }
      return this.$t('message.credit.creditScore');
    },
    progressBarData() {
      if (this.$route.meta && this.$route.meta.navInfo && this.$route.meta.numberOfSteps) {
        return {
          numberOfSteps: this.$route.meta.numberOfSteps,
          navInfo: this.$route.meta.navInfo,
        };
      }
      if (this.$route.meta && 'currentStep' in this.$route.meta) {
        let numberOfSteps = 5;
        let { currentStep } = this.$route.meta;
        const ssnEnabled = this.$store.state.business.info.creditSettings.enableSSNCapture;
        const isReturnToFinanceApp = this.$route.query.returnToFinanceApp;
        if (this.prequalificationIncomeAndDebtEnabled) {
          numberOfSteps += 2; // two extra pages for income and debt
        }
        if (this.prequalificationTradeInEquityAssessmentEnabled) {
          numberOfSteps += 2; // two extra page for trade in equity and trade InSource Is Carfax
          if (this.prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled) {
            numberOfSteps += 1; // one extra page for enhanced trade in equity
          }
          if (!this.prequalificationIncomeAndDebtEnabled) {
            // If trade in equity is enabled but income and debt is disabled
            // we need to adjust the steps to account for those pages being removed
            if (currentStep > 6) {
              currentStep -= 2;
            }
          }
        }
        if (helperFunctions.isAVFinanceFlow(this.$route.path)) {
          numberOfSteps = 3;

          if (isReturnToFinanceApp) {
            numberOfSteps = 5;
          }

          if (ssnEnabled) {
            numberOfSteps += 1;
          }

          if (isReturnToFinanceApp && ssnEnabled) {
            if (this.$route.path.includes('social-security')) {
              currentStep = 3;
            } else if (this.$route.path.includes('employer-form')) {
              currentStep = 4;
            } else if (this.$route.path.includes('mortgage-form')) {
              currentStep = 5;
            }
          } else if (ssnEnabled) {
            if (this.$route.path.includes('employer-form')) {
              currentStep = 2;
            } else if (this.$route.path.includes('mortgage-form')) {
              currentStep = 3;
            }
          } else if (isReturnToFinanceApp) {
            if (this.$route.path.includes('employer-form')) {
              currentStep = 3;
            } else if (this.$route.path.includes('mortgage-form')) {
              currentStep = 4;
            }
          }
        }
        return {
          numberOfSteps,
          navInfo: {
            from: currentStep - 1,
            to: currentStep,
          },
        };
      }
      return {};
    },
    displayVehicleName() {
      if ((this.$route.meta && !this.$route.meta.displayVehicleNameHeader) || this.showSiriusXMBanner) {
        return '';
      }
      const vehicle = this.$store.state.credit.CIMFinanceApplicationVehicle
        ? this.$store.state.credit.CIMFinanceApplicationVehicle.vehicle
        : this.$store.state.vehicle.vehicleInfo && this.$store.state.vehicle.vehicleInfo.year
        ? this.$store.state.vehicle.vehicleInfo
        : null;
      return vehicle ? helperFunctions.getVehicleName(vehicle, ['year', 'make', 'model', 'trim']) : '';
    },
  },
  mounted() {
    // A hacky way to fix the weird IOS blank page issue
    // when a user comes from DHS, credit flow is blank (but DOM is existing!)
    // Force to re-render the app
    // Only for Mobile
    if (this.isAVRMobile) {
      document.body.style.height = 'initial';
      this.$nextTick(() =>
        setTimeout(() => {
          document.body.style.height = '';
        }, 10)
      );
    }
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';
.score-text {
  font-size: 14px;
  font-weight: bold;
  color: $primary;
}
.custom-mb {
  margin-bottom: 24px;
  @media (min-width: 576px) {
    margin-bottom: 29px;
  }
}
</style>
